
import { BadgeType, BadgeDefinition, Player } from './types';
import { Home, Trophy, History, Settings, Activity, Newspaper, Medal, Layers, Briefcase, Sparkles } from 'lucide-react';
import { APP_VERSION } from './version';

// ------------------------------------------------------------------
// UI Constants
// ------------------------------------------------------------------

export const APP_NAME = "Kepnes Family Ping Pong Central";
export { APP_VERSION };

export const NAV_ITEMS = [
  { label: 'Home', icon: Home, path: '/' },
  { label: 'Rankings', icon: Activity, path: '/rankings' },
  { label: 'Past Results', icon: Layers, path: '/results' },
  { label: 'Records', icon: Medal, path: '/records' },
  { label: 'Press Room', icon: Newspaper, path: '/press' },
  { label: 'Settings', icon: Settings, path: '/admin' },
];

// ------------------------------------------------------------------
// Logic Constants
// ------------------------------------------------------------------

export const INITIAL_ELO = 50.0; // Standardized baseline ELO for new players and teams

export const ELO_K_FACTOR_PROVISIONAL = 5.0; // Increased volatility for new runs
export const ELO_K_FACTOR_ESTABLISHED = 2.5; // Increased base volatility to allow faster climbing
export const ELO_K_FACTOR_MAJOR_MULTIPLIER = 2.0; // 100% bonus volatility for Major finals
export const ELO_K_FACTOR_MINOR_MULTIPLIER = 1.5; // 50% bonus volatility for Minor finals
export const ELO_K_FACTOR_OLD_MATCH_MULTIPLIER = 0.25; // Aggressive decay: Matches > 1 year old count for 25%

// Singles Rules
export const WIN_POINTS = 21;
export const SERVE_CHANGE_POINTS = 5;

// Doubles Rules
export const WIN_POINTS_DOUBLES = 11;
export const SERVE_CHANGE_POINTS_DOUBLES = 2;

export const WIN_MARGIN = 2;

// ------------------------------------------------------------------
// Badge Definitions
// ------------------------------------------------------------------

export const BADGES: Record<string, BadgeDefinition> = {
  [BadgeType.GIANT_KILLER]: {
    id: BadgeType.GIANT_KILLER,
    name: 'Giant Killer',
    description: 'Beat a higher seeded opponent (Difference > 4) in Round 1.',
    criteria: 'Seed Diff > 4 & Round 1 Win',
    frequency: 'Repeatable',
    icon: '🗡️'
  },
  [BadgeType.MARATHON_MAN]: {
    id: BadgeType.MARATHON_MAN,
    name: 'Marathon Man',
    description: 'Win a match where score >= 27 points.',
    criteria: 'Score >= 27',
    frequency: 'Repeatable',
    icon: '🏃'
  },
  [BadgeType.THE_SWEEPER]: {
    id: BadgeType.THE_SWEEPER,
    name: 'The Sweeper',
    description: 'Win a Round Robin tournament undefeated (100% Win Pct).',
    criteria: '100% Win Rate in RR',
    frequency: 'Repeatable',
    icon: '🧹'
  },
  [BadgeType.DONUT]: {
    id: BadgeType.DONUT,
    name: 'Donut',
    description: 'Win a match 21-0.',
    criteria: 'Opponent Score 0',
    frequency: 'Repeatable',
    icon: '🍩'
  },
  [BadgeType.PICK_EM_PROPHET]: {
    id: BadgeType.PICK_EM_PROPHET,
    name: 'Pick ‘Em Prophet',
    description: 'Win the Bracket Challenge (Highest Point Total).',
    criteria: 'Highest Bracket Score',
    frequency: 'Repeatable',
    icon: '🔮'
  },
  [BadgeType.THE_LAZARUS]: {
    id: BadgeType.THE_LAZARUS,
    name: 'The Lazarus',
    description: 'Win a match after trailing by 5+ points.',
    criteria: 'Comeback > 5pts',
    frequency: 'Repeatable',
    icon: '🧟'
  },
  [BadgeType.ICE_VEINS]: {
    id: BadgeType.ICE_VEINS,
    name: 'Ice Veins',
    description: 'Win 3 consecutive matches that went to Deuce (Rolling window).',
    criteria: '3 Deuce Wins in a row',
    frequency: 'Repeatable',
    icon: '❄️'
  },
  [BadgeType.THE_WALL]: {
    id: BadgeType.THE_WALL,
    name: 'The Wall',
    description: 'Win a match where opponent scores < 5 points.',
    criteria: 'Opponent Score < 5',
    frequency: 'Repeatable',
    icon: '🧱'
  },
  [BadgeType.RENT_FREE]: {
    id: BadgeType.RENT_FREE,
    name: 'Rent Free',
    description: 'Defeat the same opponent 5 times in a row (H2H).',
    criteria: '5 Consecutive H2H Wins',
    frequency: 'Repeatable',
    icon: '🧠'
  },
  [BadgeType.IRON_MAN]: {
    id: BadgeType.IRON_MAN,
    name: 'Iron Man',
    description: 'Play 100 total matches.',
    criteria: 'Matches Played >= 100',
    frequency: 'Once',
    icon: '🤖'
  },
  [BadgeType.ROAD_WARRIOR]: {
    id: BadgeType.ROAD_WARRIOR,
    name: 'Road Warrior',
    description: 'Win a "Major" Tournament as the lowest seed.',
    criteria: 'Lowest Seed Wins Major',
    frequency: 'Repeatable',
    icon: '🛣️'
  },
  [BadgeType.CALCULATED]: {
    id: BadgeType.CALCULATED,
    name: 'Calculated',
    description: 'Win a match with exact score 21-19.',
    criteria: 'Score 21-19',
    frequency: 'Repeatable',
    icon: '📐'
  },
};
